import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Calendar, Users, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export default function StrategyComplete() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [taskCreated, setTaskCreated] = useState(false);
  const [searchParams] = useSearchParams();
  const verified = Boolean(searchParams.get('verified'));

  useEffect(() => {
    if (!verified) return;

    // Trigger confetti animation
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    // Create completion task (only if one doesn't already exist)
    const createCompletionTask = async () => {
      if (!user || taskCreated) return;

      try {
        // Check if a matching task already exists (limit 1 to avoid errors)
        const { data: matches, error: selectError } = await supabase
          .from("custom_tasks")
          .select("id")
          .eq("user_id", user.id)
          .eq("task_title", "Strategy Intake Complete ✓")
          .limit(1);

        if (selectError) throw selectError;

        // Only create if none found
        if (!matches || matches.length === 0) {
          const { error: insertError } = await supabase
            .from("custom_tasks")
            .insert({
              user_id: user.id,
              task_title: "Strategy Intake Complete ✓",
              task_description: "Your strategy intake form has been submitted successfully. Our team will review your information and reach out within 24-48 hours to discuss next steps.",
              status: "completed",
              category: "strategy",
              priority: "high",
              completed_at: new Date().toISOString(),
            });

          if (insertError) throw insertError;
        }
        
        setTaskCreated(true);
        toast.success("Strategy intake submitted successfully!");
      } catch (error) {
        console.error("Error creating completion task:", error);
      }
    };

    createCompletionTask();
  }, [user, taskCreated, verified]);

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Success Card */}
        <Card className="border-[#4CAF50]">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-[#4CAF50]" />
            </div>
            <CardTitle className="text-3xl">Strategy Intake Complete!</CardTitle>
            <CardDescription className="text-lg">
              Thank you for providing your information. We're excited to help you on your UNA journey.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* What Happens Next */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">What Happens Next?</CardTitle>
            <CardDescription>
              Here's what you can expect in the coming days
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Timeline Steps */}
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Within 24-48 Hours</h3>
                  <p className="text-muted-foreground">
                    Our team will review your strategy intake and reach out to schedule a consultation call.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Consultation Call</h3>
                  <p className="text-muted-foreground">
                    We'll discuss your specific needs, answer questions, and create a customized action plan.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Action Plan & Tasks</h3>
                  <p className="text-muted-foreground">
                    You'll receive a detailed checklist of tasks tailored to your UNA setup journey.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
              <Button 
                onClick={() => navigate("/governance/tasks")}
                className="flex-1"
              >
                View My Tasks
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                onClick={() => navigate("/journey")}
                variant="outline"
                className="flex-1"
              >
                Track My Progress
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional Resources */}
        <Card>
          <CardHeader>
            <CardTitle>In the Meantime</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              While you wait for our team to reach out, feel free to explore these resources:
            </p>
            <div className="space-y-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start"
                onClick={() => navigate("/learning")}
              >
                📚 Browse Learning Materials
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start"
                onClick={() => navigate("/library")}
              >
                📄 Explore Document Library
              </Button>
              <Button 
                variant="ghost" 
                className="w-full justify-start"
                onClick={() => navigate("/community")}
              >
                💬 Join Community Discussions
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
